import { useRef, useEffect, useState } from "react";
import { FaUpload, FaRegFileImage, FaRegFile } from "react-icons/fa";
import { BsX } from "react-icons/bs";
import Swal from "sweetalert2";

/**
 * Componente Drag & Drop reutilizable.
 * - Maneja UI, validaciones y preview
 * - Convierte File -> Base64 para desacoplar la UI del envío HTTP
 * - NO envía archivos directamente (eso lo hace el componente contenedor)
 */
export function CustomDragDrop({
  ownerLicense = [],      // Lista actual de archivos cargados (estado externo)
  onUpload,               // Callback que devuelve los nuevos archivos (en Base64)
  onDelete,               // Callback para eliminar un archivo
  count = 1,              // Cantidad máxima de archivos permitidos
  formats = []            // Extensiones permitidas (sin el punto)
}) {
  const dropContainer = useRef(null); // Contenedor del área de drop
  const fileRef = useRef(null);        // Input file oculto
  const [dragging, setDragging] = useState(false); // Estado visual de drag activo

  /**
   * Maneja tanto drag & drop como selección desde input file
   * Normaliza ambos flujos a un array de File
   */
  function handleDrop(e, source = "drop") {
    let files = [];

    if (source === "input") {
      // Selección desde el input file
      files = [...e.target.files];
    } else {
      // Drag & drop
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      files = [...e.dataTransfer.files];
    }

    if (!files.length) return;

    // Validación de cantidad máxima total
    if (ownerLicense.length + files.length > count) {
      return showAlert(
        "warning",
        "Límite de archivos",
        `Solo ${count} archivo(s) pueden cargarse`
      );
    }

    const MAX_MB = 5;

    const tooLarge = files.find(file => file.size / 1024 / 1024 > MAX_MB);

    if (tooLarge) {
      return showAlert(
        "warning",
        "Archivo demasiado grande",
        `El archivo ${tooLarge.name} excede los ${MAX_MB}MB`
      );
    }

    // Validación de extensiones permitidas
    const allFilesValid = files.every(file =>
      formats.some(ext => file.name.toLowerCase().endsWith(`.${ext}`))
    );

    if (!allFilesValid) {
      return showAlert(
        "warning",
        "Formato inválido",
        `Formatos permitidos: ${formats.join(", ").toUpperCase()}`
      );
    }

    /**
     * Conversión File -> Base64
     * Se hace acá para:
     * - permitir preview
     * - desacoplar UI del transporte (FormData / HTTP)
     */
    Promise.all(files.map(convertFileToBase64)).then(newFiles => {
      onUpload(newFiles); // Se delega el manejo del estado al contenedor
      toast.fire({ icon: "success", title: "Archivo(s) cargado(s)" });
    });
  }

  /**
   * Convierte un File a un objeto serializable con Base64
   * Este objeto NO es un File y NO debe enviarse directo al backend
   */
  function convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () =>
        resolve({
          name: file.name,
          photo: reader.result, // Base64
          type: file.type,      // MIME
          size: file.size       // Tamaño en bytes
        });
      reader.onerror = reject;
    });
  }

  /**
   * Registro manual de eventos de drag para controlar el estado visual
   */
  useEffect(() => {
    const el = dropContainer.current;
    if (!el) return;

    const over = e => {
      e.preventDefault();
      setDragging(true);
    };

    const leave = e => {
      e.preventDefault();
      setDragging(false);
    };

    el.addEventListener("dragover", over);
    el.addEventListener("dragleave", leave);
    el.addEventListener("drop", handleDrop);

    return () => {
      el.removeEventListener("dragover", over);
      el.removeEventListener("dragleave", leave);
      el.removeEventListener("drop", handleDrop);
    };
  }, [ownerLicense]);

  // Toast reutilizable para notificaciones rápidas
  const toast = Swal.mixin({
    toast: true,
    position: "bottom-end",
    timer: 3000,
    showConfirmButton: false
  });

  function showAlert(icon, title, text) {
    Swal.fire({ icon, title, text, timer: 1500, showConfirmButton: false });
  }

  // Preview simple para imágenes
  function previewImage(img) {
    Swal.fire({
      imageUrl: img,
      showCloseButton: true,
      showConfirmButton: false
    });
  }

  return (
    <>
      {/* Área de Drag & Drop */}
      <div
        ref={dropContainer}
        className={`border-2 rounded-md mt-4 py-5 text-center ${
          dragging
            ? "border-blue-400 bg-blue-50"
            : "border-dashed border-gray-300"
        }`}
      >
        <FaUpload className="mx-auto mb-2 text-gray-400" />

        {/* Input file oculto (se dispara desde el texto clickable) */}
        <input
          ref={fileRef}
          type="file"
          hidden
          multiple
          accept={formats.map(f => `.${f}`).join(",")} // Coincide con formats
          onChange={e => handleDrop(e, "input")}
        />

        <span
          className="text-blue-600 cursor-pointer"
          onClick={() => fileRef.current.click()}
        >
          Click para cargar
        </span>{" "}
        o arrastre archivos

        <div className="text-xs text-gray-400 mt-1">
          Máx {count} archivo(s): {formats.join(", ").toUpperCase()}
          <br />
          <b>Tamaño máximo por archivo : 5MB</b>
        </div>
      </div>

      {/* Listado de archivos cargados */}
      {ownerLicense.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          {ownerLicense.map((img, i) => (
            <div
              key={i}
              className="bg-slate-200 p-3 rounded-md flex justify-between"
            >
              <div
                className="flex gap-2 cursor-pointer"
                onClick={() => previewImage(img.photo)}
              >
                {img.type.startsWith("image") ? (
                  <FaRegFileImage />
                ) : (
                  <FaRegFile />
                )}
                <div>
                  <div className="text-xs">{img.name}</div>
                  <div className="text-[10px] text-gray-500">
                    {Math.floor(img.size / 1024)} KB
                  </div>
                </div>
              </div>

              {/* Eliminación delegada al componente padre */}
              <BsX className="cursor-pointer" onClick={() => onDelete(i)} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
